import { useAuth0 } from '@auth0/auth0-react'
import { RWaveLoader } from '@rgrd/component-library'
import { useEffect } from 'react'
import { Outlet } from 'react-router'

import { shouldBypassAuthForE2E } from '~/shell/config'

import styles from './RequireAuth.module.css'

/**
 * Guards authenticated routes (AUTH-04, AUTH-05).
 *
 * Mirrors outpatient's `withAuthenticationRequired` behavior as an explicit
 * wrapper: once Auth0 finishes loading, an unauthenticated user is redirected
 * to the Auth0 Universal Login via `loginWithRedirect`. The redirect happens
 * inside a `useEffect` so render stays side-effect free. The originally requested
 * relative URL — path, query, and hash — is captured in `appState.returnTo`, and the
 * `onRedirectCallback` in `Auth0ProviderWithConfig` navigates back to it after login, so
 * the user returns to the page they asked for with its filters and open flyout intact
 * rather than to the default landing route.
 *
 * Protected content is not rendered while loading or redirecting; a centered wave loading
 * indicator (labeled "Logging in…") is shown instead. When authenticated, the matched
 * child route is rendered via `<Outlet />`.
 *
 * Under the env-gated E2E bypass (`shouldBypassAuthForE2E`, set only by the
 * Playwright smoke harness) the guard renders the outlet directly without ever
 * touching Auth0 — this lets the browser smoke exercise the real shell without a
 * live session. The bypass is inert in normal dev and production.
 */
export const RequireAuth = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()

  useEffect(() => {
    if (shouldBypassAuthForE2E) {
      return
    }
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo:
            window.location.pathname +
            window.location.search +
            window.location.hash,
        },
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
      })
    }
  }, [isAuthenticated, isLoading, loginWithRedirect])

  if (!shouldBypassAuthForE2E && !isAuthenticated) {
    return (
      <div className={styles.loggingIn}>
        <RWaveLoader label="Logging in…" />
      </div>
    )
  }

  return <Outlet />
}
